import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Storefront, Plus, Check, X, ArrowSquareOut,
    ShoppingCart, Tag, CircleNotch, Package, User
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => {
    const t = localStorage.getItem('btr_session_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
};

// ═══════════════════════════════════════════════════════════════
// Onboarding Tab — Create account + check status + start onboarding
// ═══════════════════════════════════════════════════════════════
function OnboardingTab() {
    const [account, setAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [onboarding, setOnboarding] = useState(false);
    const [formData, setFormData] = useState({
        display_name: '',
        contact_email: '',
        country: 'us',
    });

    // Fetch current account status on mount
    const fetchStatus = useCallback(async () => {
        try {
            const res = await fetch(`${API}/connect/accounts/me`, {
                credentials: 'include', headers: getH()
            });
            if (res.ok) {
                const data = await res.json();
                setAccount(data);
            }
        } catch (e) {
            console.error('Account status fetch error:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    // Create a new connected account
    const createAccount = async () => {
        if (!formData.display_name || !formData.contact_email) {
            toast.error('Name and email are required');
            return;
        }
        setCreating(true);
        try {
            const res = await fetch(`${API}/connect/accounts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify(formData),
            });
            if (res.ok) {
                toast.success('Connected account created! Complete onboarding to start selling.');
                fetchStatus();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to create account');
            }
        } catch { toast.error('Network error'); }
        finally { setCreating(false); }
    };

    // Start onboarding flow
    const startOnboarding = async () => {
        setOnboarding(true);
        try {
            const res = await fetch(`${API}/connect/accounts/onboarding-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ origin_url: window.location.origin }),
            });
            if (res.ok) {
                const data = await res.json();
                // Redirect to Stripe's hosted onboarding
                window.location.href = data.url;
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to create onboarding link');
            }
        } catch { toast.error('Network error'); }
        finally { setOnboarding(false); }
    };

    if (loading) {
        return (
            <Card className="glass-card">
                <CardContent className="p-6 flex items-center justify-center">
                    <CircleNotch size={24} className="animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    // If user doesn't have a connected account yet, show the create form
    if (!account?.has_account) {
        return (
            <Card className="glass-card" data-testid="create-connect-account">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Storefront size={22} className="text-primary" />
                        </div>
                        <div>
                            <h3 className="font-display font-bold text-foreground">Become a Seller</h3>
                            <p className="text-xs text-muted-foreground">Create a connected account to sell on Boltorium</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Display Name</label>
                            <Input
                                value={formData.display_name}
                                onChange={e => setFormData(f => ({ ...f, display_name: e.target.value }))}
                                placeholder="Your seller name"
                                data-testid="connect-display-name"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Contact Email</label>
                            <Input
                                type="email"
                                value={formData.contact_email}
                                onChange={e => setFormData(f => ({ ...f, contact_email: e.target.value }))}
                                placeholder="seller@example.com"
                                data-testid="connect-email"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Country</label>
                            <select
                                value={formData.country}
                                onChange={e => setFormData(f => ({ ...f, country: e.target.value }))}
                                className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm"
                                data-testid="connect-country"
                            >
                                <option value="us">United States</option>
                                <option value="ca">Canada</option>
                                <option value="gb">United Kingdom</option>
                                <option value="au">Australia</option>
                                <option value="de">Germany</option>
                                <option value="fr">France</option>
                            </select>
                        </div>
                    </div>

                    <Button
                        onClick={createAccount}
                        disabled={creating}
                        className="w-full btn-primary"
                        data-testid="create-connect-btn"
                    >
                        {creating ? <CircleNotch size={18} className="animate-spin mr-2" /> : <Storefront size={18} className="mr-2" />}
                        {creating ? 'Creating...' : 'Create Connected Account'}
                    </Button>
                </CardContent>
            </Card>
        );
    }

    // User has a connected account — show status + onboarding button
    return (
        <Card className="glass-card" data-testid="connect-account-status">
            <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            account.ready_to_receive_payments ? 'bg-neon-green/10' : 'bg-boltz-gold/10'
                        }`}>
                            {account.ready_to_receive_payments
                                ? <Check size={22} className="text-neon-green" weight="bold" />
                                : <Storefront size={22} className="text-boltz-gold" />
                            }
                        </div>
                        <div>
                            <h3 className="font-display font-bold text-foreground">{account.display_name}</h3>
                            <p className="text-xs text-muted-foreground font-mono">{account.stripe_account_id}</p>
                        </div>
                    </div>
                    <Button
                        size="sm" variant="ghost"
                        onClick={fetchStatus}
                        className="text-xs text-muted-foreground"
                        data-testid="refresh-status-btn"
                    >
                        Refresh
                    </Button>
                </div>

                {/* Status indicators */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-white/5 border border-white/5">
                        <p className="text-[10px] text-muted-foreground font-display">ONBOARDING</p>
                        <div className="flex items-center gap-1.5 mt-1">
                            {account.onboarding_complete
                                ? <><Check size={14} className="text-neon-green" /><span className="text-sm text-neon-green font-bold">Complete</span></>
                                : <><X size={14} className="text-boltz-gold" /><span className="text-sm text-boltz-gold font-bold">Incomplete</span></>
                            }
                        </div>
                    </div>
                    <div className="p-3 rounded-xl bg-white/5 border border-white/5">
                        <p className="text-[10px] text-muted-foreground font-display">PAYMENTS</p>
                        <div className="flex items-center gap-1.5 mt-1">
                            {account.ready_to_receive_payments
                                ? <><Check size={14} className="text-neon-green" /><span className="text-sm text-neon-green font-bold">Active</span></>
                                : <><X size={14} className="text-plasma-red" /><span className="text-sm text-plasma-red font-bold">Not Ready</span></>
                            }
                        </div>
                    </div>
                </div>

                {/* Onboarding button if not complete */}
                {!account.onboarding_complete && (
                    <Button
                        onClick={startOnboarding}
                        disabled={onboarding}
                        className="w-full bg-boltz-gold text-black font-display hover:bg-boltz-gold/80"
                        data-testid="start-onboarding-btn"
                    >
                        {onboarding ? <CircleNotch size={18} className="animate-spin mr-2" /> : <ArrowSquareOut size={18} className="mr-2" />}
                        {onboarding ? 'Redirecting...' : 'Onboard to Collect Payments'}
                    </Button>
                )}

                {account.requirements_status && (
                    <p className="text-xs text-muted-foreground text-center">
                        Requirements status: <span className="font-mono text-foreground">{account.requirements_status}</span>
                    </p>
                )}
            </CardContent>
        </Card>
    );
}


// ═══════════════════════════════════════════════════════════════
// Products Tab — Create products linked to connected account
// ═══════════════════════════════════════════════════════════════
function ProductsTab() {
    const [products, setProducts] = useState([]);
    const [account, setAccount] = useState(null);
    const [creating, setCreating] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        currency: 'usd',
    });

    useEffect(() => {
        fetch(`${API}/connect/accounts/me`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.has_account) setAccount(d); });
        fetch(`${API}/connect/products`, { credentials: 'include', headers: getH() })
            .then(r => r.ok ? r.json() : [])
            .then(setProducts);
    }, []);

    const createProduct = async () => {
        if (!formData.name || !formData.price) {
            toast.error('Name and price are required');
            return;
        }
        if (!account?.stripe_account_id) {
            toast.error('You need a connected account first');
            return;
        }
        setCreating(true);
        try {
            const res = await fetch(`${API}/connect/products`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({
                    name: formData.name,
                    description: formData.description,
                    price_cents: Math.round(parseFloat(formData.price) * 100),
                    currency: formData.currency,
                    connected_account_id: account.stripe_account_id,
                }),
            });
            if (res.ok) {
                const product = await res.json();
                setProducts(prev => [product, ...prev]);
                setFormData({ name: '', description: '', price: '', currency: 'usd' });
                setShowForm(false);
                toast.success('Product created!');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to create product');
            }
        } catch { toast.error('Network error'); }
        finally { setCreating(false); }
    };

    const myProducts = products.filter(p => p.connected_account_id === account?.stripe_account_id);

    return (
        <div className="space-y-4">
            {!account?.has_account ? (
                <Card className="glass-card">
                    <CardContent className="p-6 text-center">
                        <Storefront size={48} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">Create a connected account first to add products</p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="flex items-center justify-between">
                        <h3 className="font-display text-sm text-foreground">Your Products ({myProducts.length})</h3>
                        <Button size="sm" onClick={() => setShowForm(!showForm)} className="text-xs btn-primary" data-testid="toggle-product-form">
                            <Plus size={14} className="mr-1" />{showForm ? 'Cancel' : 'Add Product'}
                        </Button>
                    </div>

                    {showForm && (
                        <Card className="glass-card border-primary/20" data-testid="product-form">
                            <CardContent className="p-4 space-y-3">
                                <Input value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                                    placeholder="Product name" data-testid="product-name" />
                                <Input value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                                    placeholder="Description (optional)" data-testid="product-description" />
                                <div className="flex gap-2">
                                    <Input type="number" step="0.01" min="0.50" value={formData.price}
                                        onChange={e => setFormData(f => ({ ...f, price: e.target.value }))}
                                        placeholder="Price (e.g. 9.99)" className="font-mono flex-1" data-testid="product-price" />
                                    <select value={formData.currency} onChange={e => setFormData(f => ({ ...f, currency: e.target.value }))}
                                        className="bg-card border border-border rounded-lg px-3 py-2 text-sm w-24">
                                        <option value="usd">USD</option>
                                        <option value="cad">CAD</option>
                                        <option value="eur">EUR</option>
                                        <option value="gbp">GBP</option>
                                    </select>
                                </div>
                                <Button onClick={createProduct} disabled={creating} className="w-full btn-primary" data-testid="create-product-btn">
                                    {creating ? 'Creating...' : 'Create Product'}
                                </Button>
                            </CardContent>
                        </Card>
                    )}

                    {myProducts.length === 0 && !showForm ? (
                        <Card className="glass-card">
                            <CardContent className="p-8 text-center">
                                <Package size={48} className="text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">No products yet. Add your first product!</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-2">
                            {myProducts.map(p => (
                                <Card key={p.stripe_product_id} className="glass-card">
                                    <CardContent className="p-3 flex items-center justify-between">
                                        <div>
                                            <p className="font-bold text-sm">{p.name}</p>
                                            {p.description && <p className="text-xs text-muted-foreground truncate max-w-[250px]">{p.description}</p>}
                                        </div>
                                        <Badge className="font-mono text-neon-gold bg-neon-gold/10 border-neon-gold/20">
                                            ${(p.price_cents / 100).toFixed(2)} {p.currency?.toUpperCase()}
                                        </Badge>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}


// ═══════════════════════════════════════════════════════════════
// Storefront Tab — Browse all products, buy from any seller
// ═══════════════════════════════════════════════════════════════
function StorefrontTab() {
    const [products, setProducts] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(null);

    useEffect(() => {
        Promise.all([
            fetch(`${API}/connect/products`).then(r => r.ok ? r.json() : []),
            fetch(`${API}/connect/accounts`).then(r => r.ok ? r.json() : []),
        ]).then(([prods, accts]) => {
            setProducts(prods);
            setAccounts(accts);
        }).finally(() => setLoading(false));
    }, []);

    const buyProduct = async (productId) => {
        setPurchasing(productId);
        try {
            const res = await fetch(`${API}/connect/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({
                    product_id: productId,
                    quantity: 1,
                    origin_url: window.location.origin,
                }),
            });
            if (res.ok) {
                const data = await res.json();
                window.location.href = data.checkout_url;
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Checkout failed');
            }
        } catch { toast.error('Network error'); }
        finally { setPurchasing(null); }
    };

    const accountMap = Object.fromEntries(accounts.map(a => [a.stripe_account_id, a]));

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <CircleNotch size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Sellers */}
            {accounts.length > 0 && (
                <div>
                    <h3 className="font-display text-xs text-muted-foreground mb-2">SELLERS</h3>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        {accounts.map(a => (
                            <Badge key={a.stripe_account_id} variant="outline" className="text-xs whitespace-nowrap border-primary/20 text-primary">
                                <User size={10} className="mr-1" />{a.display_name}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}

            {/* Products grid */}
            {products.length === 0 ? (
                <Card className="glass-card">
                    <CardContent className="p-8 text-center">
                        <ShoppingCart size={48} className="text-muted-foreground mx-auto mb-3" />
                        <p className="font-medium">No products listed yet</p>
                        <p className="text-sm text-muted-foreground mt-1">Sellers can add products in the Products tab</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {products.map(p => {
                        const seller = accountMap[p.connected_account_id];
                        return (
                            <Card key={p.stripe_product_id} className="glass-card overflow-hidden hover:border-primary/20 transition-colors"
                                data-testid={`storefront-product-${p.stripe_product_id}`}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                            <h4 className="font-bold text-sm text-foreground">{p.name}</h4>
                                            {p.description && (
                                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{p.description}</p>
                                            )}
                                        </div>
                                        <Badge className="font-mono text-neon-gold bg-neon-gold/10 border-neon-gold/20 ml-2 shrink-0">
                                            ${(p.price_cents / 100).toFixed(2)}
                                        </Badge>
                                    </div>

                                    {seller && (
                                        <p className="text-[10px] text-muted-foreground mb-3">
                                            Sold by <span className="text-primary">{seller.display_name}</span>
                                        </p>
                                    )}

                                    <Button
                                        onClick={() => buyProduct(p.stripe_product_id)}
                                        disabled={purchasing === p.stripe_product_id}
                                        className="w-full btn-primary text-xs"
                                        data-testid={`buy-${p.stripe_product_id}`}
                                    >
                                        {purchasing === p.stripe_product_id
                                            ? <CircleNotch size={14} className="animate-spin mr-1" />
                                            : <ShoppingCart size={14} className="mr-1" />
                                        }
                                        {purchasing === p.stripe_product_id ? 'Redirecting...' : 'Buy Now'}
                                    </Button>

                                    <p className="text-[10px] text-muted-foreground text-center mt-2">
                                        10% platform fee on card sales · Powered by Stripe
                                    </p>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


// ═══════════════════════════════════════════════════════════════
// Main Connect Page
// ═══════════════════════════════════════════════════════════════
export default function StripeConnect() {
    const [searchParams] = useSearchParams();
    const checkoutStatus = searchParams.get('checkout');

    useEffect(() => {
        if (checkoutStatus === 'success') {
            toast.success('Purchase successful! The seller will receive your payment.');
        } else if (checkoutStatus === 'cancelled') {
            toast.info('Checkout was cancelled.');
        }
    }, [checkoutStatus]);

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-boltz-gold text-glow-gold">CONNECT</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground"
                        data-testid="connect-title">
                        Seller Hub
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1" style={{ fontFamily: 'var(--font-body)' }}>
                        Sell products on Boltorium and get paid directly via Stripe Connect
                    </p>
                </motion.div>

                <Tabs defaultValue="onboarding" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 mb-6">
                        <TabsTrigger value="onboarding" data-testid="tab-onboarding">
                            <Storefront size={16} className="mr-2" />Account
                        </TabsTrigger>
                        <TabsTrigger value="products" data-testid="tab-products">
                            <Tag size={16} className="mr-2" />Products
                        </TabsTrigger>
                        <TabsTrigger value="storefront" data-testid="tab-storefront">
                            <ShoppingCart size={16} className="mr-2" />Storefront
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="onboarding">
                        <OnboardingTab />
                    </TabsContent>

                    <TabsContent value="products">
                        <ProductsTab />
                    </TabsContent>

                    <TabsContent value="storefront">
                        <StorefrontTab />
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}
